import { useCallback, useEffect, useRef, useState } from 'react'
import type { BarcodeFormat } from '@/lib/api-types'
import { report } from '@/lib/telemetry'
import { computeRoi, roiPointToVideo, type Roi } from './roi'
import { familiesFor, formatsInFamily, type FormatFamily } from './formats'
import DecoderWorker from './decoder.worker?worker'
import type { DecodedBarcode, MainMessage, WorkerMessage } from './decoder-protocol'

export interface ScanHit {
  text: string
  format: BarcodeFormat | null
  /** Corners in video-space, ready for the overlay. */
  corners: { x: number; y: number }[]
}

export interface DecoderStats {
  /** Rolling average decode time, for the "try moving closer" coaching. */
  avgDecodeMs: number
  attempts: number
  /** Consecutive frames with no hit. Drives the coaching hints. */
  misses: number
}

interface Options {
  formats: readonly BarcodeFormat[]
  zoom: number
  onHit: (hit: ScanHit) => void
  enabled: boolean
}

/**
 * Feeds cropped frames to the decode worker and reports hits.
 *
 * Memory discipline is the point of most of this file. iOS kills a tab that
 * churns memory, which is the most likely explanation for the old app
 * "suddenly resetting after several successive scans": it allocated a fresh
 * canvas and full-resolution ImageData per frame. Here there is exactly one
 * canvas, one in-flight decode, and the pixel buffer is transferred to the
 * worker rather than copied.
 */
export function useDecoder({ formats, zoom, onHit, enabled }: Options) {
  const [ready, setReady] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [stats, setStats] = useState<DecoderStats>({ avgDecodeMs: 0, attempts: 0, misses: 0 })

  const workerRef = useRef<Worker | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const ctxRef = useRef<CanvasRenderingContext2D | null>(null)

  const inFlight = useRef(false)
  const requestId = useRef(0)
  const lastRoi = useRef<Roi | null>(null)
  const familyCursor = useRef(0)
  const missStreak = useRef(0)
  const avgMs = useRef(0)
  const attempts = useRef(0)

  // Held in a ref so the frame pump never has to be torn down and rebuilt just
  // because the callback identity changed.
  const onHitRef = useRef(onHit)
  onHitRef.current = onHit

  useEffect(() => {
    const worker = new DecoderWorker()
    workerRef.current = worker

    worker.onmessage = (event: MessageEvent<WorkerMessage>) => {
      const msg = event.data

      switch (msg.type) {
        case 'ready':
          setReady(true)
          break

        case 'init-failed':
          setError(msg.message)
          report({ level: 'error', event: 'decoder.init_failed', message: msg.message })
          break

        case 'result': {
          inFlight.current = false
          attempts.current += 1
          // Exponential moving average — cheap, and it tracks a change in
          // conditions (light, distance) rather than the whole session.
          avgMs.current = avgMs.current === 0 ? msg.elapsedMs : avgMs.current * 0.8 + msg.elapsedMs * 0.2

          const first = msg.barcodes[0]
          if (first !== undefined) {
            missStreak.current = 0
            emitHit(first)
          } else {
            missStreak.current += 1
          }

          setStats({
            avgDecodeMs: Math.round(avgMs.current),
            attempts: attempts.current,
            misses: missStreak.current,
          })
          break
        }

        case 'error':
          inFlight.current = false
          missStreak.current += 1
          report({ level: 'warn', event: 'decoder.decode_failed', message: msg.message })
          break
      }
    }

    worker.onerror = (event) => {
      inFlight.current = false
      setError(event.message)
      report({ level: 'error', event: 'decoder.worker_error', message: event.message })
    }

    worker.postMessage({ type: 'init' } satisfies MainMessage)

    return () => {
      worker.onmessage = null
      worker.onerror = null
      worker.terminate()
      workerRef.current = null
      setReady(false)
    }
  }, [])

  function emitHit(barcode: DecodedBarcode): void {
    const roi = lastRoi.current
    if (roi === null) return
    onHitRef.current({
      text: barcode.text,
      format: barcode.format,
      corners: barcode.corners.map((p) => roiPointToVideo(roi, p.x, p.y)),
    })
  }

  /**
   * Grab one frame and hand it to the worker.
   *
   * Called from the video frame callback. Returns immediately when a decode is
   * still running: queueing frames behind a slow decode builds a backlog that
   * shows stale results and grows memory without improving anything.
   */
  const pump = useCallback(
    (video: HTMLVideoElement) => {
      const worker = workerRef.current
      if (worker === null || !ready || inFlight.current || !enabled) return

      const vw = video.videoWidth
      const vh = video.videoHeight
      if (vw === 0 || vh === 0) return

      // Alternate families frame by frame rather than decoding both per frame.
      // A QR-only workflow returns a single family and so never pays for the 1D
      // pass at all.
      const families = familiesFor(formats)
      if (families.length === 0) return
      const family: FormatFamily = families[familyCursor.current % families.length]!
      familyCursor.current += 1

      const roi = computeRoi(family, vw, vh, zoom)
      if (roi.dw === 0 || roi.dh === 0) return
      lastRoi.current = roi

      let canvas = canvasRef.current
      if (canvas === null) {
        canvas = document.createElement('canvas')
        canvasRef.current = canvas
        // willReadFrequently keeps the surface in software memory; without it
        // every getImageData forces a GPU→CPU readback stall.
        ctxRef.current = canvas.getContext('2d', { willReadFrequently: true })
      }
      const ctx = ctxRef.current
      if (ctx === null) return

      // Resize only when the shape actually changes — reassigning width/height
      // reallocates the backing store, which is the allocation we are avoiding.
      if (canvas.width !== roi.dw || canvas.height !== roi.dh) {
        canvas.width = roi.dw
        canvas.height = roi.dh
      }

      // Crop and downscale in a single GPU operation.
      ctx.drawImage(video, roi.sx, roi.sy, roi.sw, roi.sh, 0, 0, roi.dw, roi.dh)

      let imageData: ImageData
      try {
        imageData = ctx.getImageData(0, 0, roi.dw, roi.dh)
      } catch (err) {
        // A cross-origin frame taints the canvas. Cannot happen with
        // getUserMedia, but failing loudly beats an infinite silent stall.
        setError('Could not read camera frames.')
        report({
          level: 'error',
          event: 'decoder.getimagedata_failed',
          message: err instanceof Error ? err.message : String(err),
        })
        return
      }

      inFlight.current = true
      requestId.current += 1

      const request: MainMessage = {
        type: 'decode',
        id: requestId.current,
        data: imageData.data.buffer as ArrayBuffer,
        width: roi.dw,
        height: roi.dh,
        formats: formatsInFamily(formats, family),
        // Escalate only once the easy pass has plainly failed. Running the slow
        // path from the first frame would make the common case worse.
        tryHarder: missStreak.current > 8,
      }

      // Transfer, don't copy: ownership of the pixel buffer moves to the
      // worker, so nothing is duplicated per frame.
      worker.postMessage(request, [request.data])
    },
    [enabled, formats, ready, zoom],
  )

  const reset = useCallback(() => {
    missStreak.current = 0
    avgMs.current = 0
    attempts.current = 0
    inFlight.current = false
    setStats({ avgDecodeMs: 0, attempts: 0, misses: 0 })
  }, [])

  /** Drop the scratch canvas when the scanner closes, rather than holding it. */
  const releaseBuffers = useCallback(() => {
    canvasRef.current = null
    ctxRef.current = null
    lastRoi.current = null
  }, [])

  return { ready, error, stats, pump, reset, releaseBuffers }
}
